// Owner: admin-ops (settings cleanup). Optional workspace modules: features that only some workspaces need
// (activities on record timelines, meeting scheduling, the meeting recorder) and that an admin turns on in
// Settings › General, or a blueprint turns on when it is installed. A module that is off has no screens, no
// API (409 `module_off`) and no background work. Browser-safe: shapes and pure helpers only (re-exported by
// `@dataroom/core/access` next to the general settings).
import { z } from 'zod';
import { propertyOf } from './json';

export const WORKSPACE_MODULES = ['activities', 'meetings', 'recorder'] as const;
export const WorkspaceModule = z.enum(WORKSPACE_MODULES);
export type WorkspaceModule = z.infer<typeof WorkspaceModule>;

/** What a module needs: the recorder joins the meetings that scheduling and the calendars bring. */
export const MODULE_REQUIRES: Readonly<Record<WorkspaceModule, readonly WorkspaceModule[]>> = {
  activities: [],
  meetings: [],
  recorder: ['meetings'],
};

/** A set of modules in canonical order, each with what it needs (turning a module on turns its needs on). */
export function normalizeModules(modules: Iterable<WorkspaceModule>): WorkspaceModule[] {
  const on = new Set<WorkspaceModule>();
  const add = (m: WorkspaceModule) => {
    if (on.has(m)) return;
    on.add(m);
    for (const need of MODULE_REQUIRES[m]) add(need);
  };
  for (const m of modules) add(m);
  return WORKSPACE_MODULES.filter((m) => on.has(m));
}

/**
 * The modules after switching one: on brings what it needs (recorder → meetings), off takes what needs it
 * along (meetings off → recorder off).
 */
export function switchModule(
  modules: Iterable<WorkspaceModule>,
  module: WorkspaceModule,
  on: boolean,
): WorkspaceModule[] {
  const current = new Set(modules);
  if (on) return normalizeModules([...current, module]);
  const off = new Set<WorkspaceModule>([module]);
  // Whatever needs a module that goes off goes off too (transitively).
  for (let grew = true; grew;) {
    grew = false;
    for (const m of WORKSPACE_MODULES)
      if (!off.has(m) && MODULE_REQUIRES[m].some((need) => off.has(need))) {
        off.add(m);
        grew = true;
      }
  }
  return normalizeModules([...current].filter((m) => !off.has(m)));
}

/** The stored setting (`settings.modules`): anything unreadable counts as no module. */
export function readModules(value: unknown): WorkspaceModule[] {
  const enabled = propertyOf(value, 'enabled');
  const list = Array.isArray(value) ? value : Array.isArray(enabled) ? enabled : [];
  return normalizeModules(list.filter((m): m is WorkspaceModule => WorkspaceModule.safeParse(m).success));
}

/** Settings › General › Modules: the modules to have on (what they need is turned on with them). */
export const UpdateWorkspaceModulesInput = z.object({ modules: z.array(WorkspaceModule).max(10) }).strict();
export type UpdateWorkspaceModulesInput = z.infer<typeof UpdateWorkspaceModulesInput>;

export const WorkspaceModulesDTO = z.object({ modules: z.array(WorkspaceModule) });
export type WorkspaceModulesDTO = z.infer<typeof WorkspaceModulesDTO>;

const MODULE_NAMES: Record<'en' | 'de', Record<WorkspaceModule, string>> = {
  en: { activities: 'Activities', meetings: 'Meetings', recorder: 'Meeting recorder' },
  de: { activities: 'Aktivitäten', meetings: 'Besprechungen', recorder: 'Besprechungsaufzeichnung' },
};

/** `details.reason` of the 409 an API route of a module that is off answers (`details.module` names it). */
export const MODULE_OFF = 'module_off';

/** The message of the 409 of a module that is off. */
export function moduleOffMessage(module: WorkspaceModule, language: string = 'en'): string {
  if (language === 'de')
    return `Das Modul „${MODULE_NAMES.de[module]}“ ist in diesem Arbeitsbereich ausgeschaltet. Ein Admin kann es unter Einstellungen › Allgemein › Module einschalten.`;
  return `The ${MODULE_NAMES.en[module]} module is turned off in this workspace. An admin can turn it on in Settings › General › Modules.`;
}
